/**
 * ScrollToTop — toute navigation vers une nouvelle page atterrit en haut.
 *
 * LE PROBLÈME
 * -----------
 * `BrowserRouter` ne touche jamais au scroll : changer de route ne fait
 * que remplacer l'arbre React, la fenêtre reste exactement où elle était.
 * Quelqu'un qui descendait au pied de /plans et cliquait « Comment ça
 * marche » dans le footer arrivait donc à 2 000 px du haut de la nouvelle
 * page — au milieu, parfois en dessous du contenu.
 *
 * Pourquoi pas `<ScrollRestoration>` de react-router : il n'existe que
 * pour les routeurs de DONNÉES (`createBrowserRouter`). L'app monte
 * `<BrowserRouter>` + `<Routes>` en mode déclaratif, où le composant n'est
 * pas monté. Passer au routeur de données serait une refonte du routage,
 * hors sujet ici.
 *
 * LES TROIS CAS, ET CE QU'ILS DONNENT
 * -----------------------------------
 * · PUSH / REPLACE (un clic sur un lien) → haut de page, instantané.
 * · POP (bouton retour / suivant)        → la position d'AVANT est
 *   restaurée, c'est-à-dire le comportement que tout le monde attend d'un
 *   bouton retour. `useNavigationType()` donne la distinction directement,
 *   donc on la fait plutôt que de la documenter comme non faite.
 * · Une ANCRE (`/page#section`)          → on va à la section, pas en
 *   haut. C'est la seule chose que l'auteur du lien a demandée.
 *
 * INSTANTANÉ, JAMAIS `smooth` — ET IL FAUT LE DIRE EXPLICITEMENT
 * -------------------------------------------------------------
 * `styles/home.css` pose `html { scroll-behavior: smooth }`, et cette
 * règle n'est pas scopée : elle s'applique à TOUT le site. Or un
 * `window.scrollTo(0, 0)` — comme un `scrollTo({ top: 0 })` — vaut
 * `behavior: 'auto'`, et `auto` se résout sur la valeur CSS. Autrement
 * dit, sur ce repo, les deux formes ANIMENT le défilement.
 *
 * Ça s'observe : après un `window.scrollTo(0, 99999)`, `window.scrollY`
 * lu dans la foulée vaut encore 0, parce que l'animation vient seulement
 * de démarrer. Un retour en haut depuis 2 000 px durerait le temps d'une
 * animation, pendant que la nouvelle page est déjà affichée — exactement
 * le mal de mer qu'on veut éviter.
 *
 * D'où `behavior: 'instant'`, la seule valeur qui court-circuite le CSS.
 * Les défilements INTERNES légitimes (le chat qui suit ses messages, les
 * boutons d'ancre d'une même page) gardent leur `smooth` : ils ne passent
 * pas par ici.
 *
 * LE CONTENU QUI ARRIVE APRÈS
 * ---------------------------
 * Une page de dossier fait 300 px de haut au premier rendu, puis 4 000
 * une fois l'API revenue. Restaurer 2 500 px à cet instant-là ne fait
 * rien : le navigateur borne à la hauteur courante. Les deux cas qui
 * visent une position précise (restauration POP, ancre) réessaient donc
 * sur quelques frames, jusqu'à ce que la cible soit atteignable ou que le
 * délai tombe. Le cas « haut de page », lui, n'a rien à attendre.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** Au-delà, on renonce : le contenu ne viendra plus, ou l'ancre n'existe pas. */
const SETTLE_DEADLINE_MS = 1200;

/**
 * L'élément visé par un hash, ou null.
 *
 * `#session_id=…` (le retour OAuth) et tout hash qui n'est pas un
 * sélecteur valide passeraient `querySelector` à la moulinette et
 * lèveraient : d'où `getElementById` d'abord, qui ne lève jamais, et le
 * `try` autour du reste.
 */
function elementForHash(hash) {
  const raw = decodeURIComponent(hash.slice(1));
  if (!raw) return null;
  const byId = document.getElementById(raw);
  if (byId) return byId;
  try {
    return document.querySelector(hash);
  } catch (_) {
    return null;
  }
}

export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  // Position de chaque entrée d'historique, indexée par sa `key` — c'est
  // l'identifiant que react-router attribue à chaque entrée, et il survit
  // aux allers-retours. Un `Map` en ref : on ne re-rend jamais pour ça.
  const positions = useRef(new Map());
  const currentKey = useRef(key);
  // Une seule boucle de rattrapage à la fois : une navigation rapide ne
  // doit pas laisser la précédente continuer à déplacer l'écran.
  const settleFrame = useRef(0);

  // On prend la main sur la restauration du navigateur. En 'auto', il
  // tente de restaurer lui-même sur POP, au moment où React n'a encore
  // rien rendu : il tombe sur une page vide, ne restaure rien, puis nous
  // regarde faire. Deux mécanismes qui visent la même chose et se gênent.
  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previous; };
  }, []);

  // Mémorisation continue de la position de l'entrée courante.
  //
  // Enregistrer au moment de la navigation serait trop tard : React a
  // déjà remplacé l'arbre, la page peut être plus courte, et le navigateur
  // a déjà borné `scrollY`. On note donc au fil du défilement.
  // Volontairement synchrone plutôt que dans un `requestAnimationFrame` :
  // une frame de retard suffirait à écrire la position de l'ancienne page
  // sous la clé de la nouvelle.
  useEffect(() => {
    const onScroll = () => { positions.current.set(currentKey.current, window.scrollY); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useLayoutEffect(() => {
    currentKey.current = key;

    if (settleFrame.current) {
      cancelAnimationFrame(settleFrame.current);
      settleFrame.current = 0;
    }

    /** Réessaie jusqu'à ce que `attempt()` réussisse, ou que le délai tombe. */
    const settle = (attempt) => {
      const deadline = Date.now() + SETTLE_DEADLINE_MS;
      const tick = () => {
        settleFrame.current = 0;
        if (attempt()) return;
        if (Date.now() < deadline) settleFrame.current = requestAnimationFrame(tick);
      };
      tick();
    };

    // ── a. Une ancre gagne toujours ────────────────────────────────
    if (hash && hash.length > 1) {
      settle(() => {
        const el = elementForHash(hash);
        if (!el) return false;
        el.scrollIntoView({ block: 'start', behavior: 'instant' });
        return true;
      });
      return undefined;
    }

    // ── b. Retour / suivant : on restaure ──────────────────────────
    if (navigationType === 'POP') {
      const saved = positions.current.get(key) || 0;
      if (saved > 0) {
        settle(() => {
          window.scrollTo({ top: saved, left: 0, behavior: 'instant' });
          // Tant que la page est trop courte, le navigateur borne et on
          // atterrit plus haut que demandé : c'est le signal qu'il faut
          // attendre le contenu suivant.
          return Math.abs(window.scrollY - saved) <= 2;
        });
        return undefined;
      }
    }

    // ── c. Nouvelle page : en haut, tout de suite ──────────────────
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    return undefined;
  }, [pathname, hash, key, navigationType]);

  // Nettoyage au démontage — la boucle ne doit pas survivre au composant.
  useEffect(() => () => {
    if (settleFrame.current) cancelAnimationFrame(settleFrame.current);
  }, []);

  return null;
}
